import React, { useEffect, useState, useRef } from 'react';
//@ts-ignore
import * as styles from './product-support-layout.module.css';
import {
    MediaData,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils/index';
import LazyImage from '../Global/LazyImage';
import Modal from '../Global/LazyModal';
import { Faq as FaqType } from '../Global/DataUtils/faqQueries';
import Faq, { isRenderableFaq } from '../Faq/index';
import {
    ParsedDocument,
    ParsedQuickTile,
    ParsedSupportVideo,
    DocumentGroup,
} from './productSupportTypes';
import ShowMoreArrow from '../Global/ShowMoreArrow';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import VideoPlayer from '../Global/VideoPlayer';
import { KontentStringValue } from '../../../types/KontentProps';
import { mapDocumentGroups } from './productSupportUtils';

import ReactPlayer from '../Global/LazyReactPlayer';

interface Props {
    pageMetaData?: {
        displayPageName: string | undefined;
    };
    productName: string;
    documentImage: MediaData | null | undefined;
    documents: ParsedDocument[];
    documentGroups: DocumentGroup[];
    quickTiles: ParsedQuickTile[] | null;
    videos: ParsedSupportVideo[] | null;
    faqs: FaqType[] | null;
    faqanchor: string | undefined;
    maxQuestionToDisplay: number | undefined;
    headline?: string | undefined;
    kontentItemId?: string;
}

const ProductSupportLayout: React.FC<Props> = ({
    pageMetaData,
    productName,
    documentImage,
    documents,
    documentGroups,
    quickTiles,
    videos,
    faqs,
    faqanchor,
    maxQuestionToDisplay,
    headline,
    kontentItemId,
    ...props
}) => {
    const bp = useBreakpoint();
    const mobileFaqs = 3;
    const desktopFaqs = 5;
    const faqsToShow = maxQuestionToDisplay
        ? maxQuestionToDisplay
        : bp.md
        ? desktopFaqs
        : mobileFaqs;
    const [modalActive, setModalActive] = useState(false);
    const [activeVideo, setActiveVideo] = useState('');
    const safeFaqs = Array.isArray(faqs) ? faqs.filter(isRenderableFaq) : [];
    const [visibleFaqCount, setVisibleFaqCount] = useState(() =>
        Math.max(0, faqsToShow || 0)
    );
    const [currentFaqPage, setCurrentFaqPage] = useState(1);
    const showMoreColor = '#505251';

    const headingRef = useRef(null);

    useEffect(() => {
        const handleFormLoad = () => {
            const hash = window.location.hash;
            if (faqanchor && hash === `#${faqanchor}` && headingRef.current) {
                setTimeout(() => {
                    headingRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }, 3000);
            }
        };

        if (faqanchor) {
            window.addEventListener('formLoaded', handleFormLoad);
        }

        handleFormLoad();

        return () => {
            if (faqanchor) {
                window.removeEventListener('formLoaded', handleFormLoad);
            }
        };
    }, [faqanchor]);

    useEffect(() => {
        setVisibleFaqCount(Math.max(0, faqsToShow || 0));
        setCurrentFaqPage(1);
    }, [faqsToShow]);

    const handleViewMoreClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newIdx = currentFaqPage + 1;
        setVisibleFaqCount(Math.max(0, (faqsToShow || 0) * newIdx));
        setCurrentFaqPage(newIdx);
    };

    const renderViewMore = () => {
        if (safeFaqs.length > visibleFaqCount) {
            return (
                <ShowMoreArrow
                    btnText="VIEW MORE"
                    strokeColor={showMoreColor}
                    transform="none"
                    handleClick={handleViewMoreClick}
                />
            );
        }
    };

    const openModal = (videoUrl: string) => {
        setModalActive(true);
        setActiveVideo(videoUrl);
    };

    const closeModal = () => {
        setModalActive(false);
    };

    const mappedDocumentGroups = React.useMemo(
        () => mapDocumentGroups(documentGroups),
        [documentGroups]
    );

    return (
        <section
            className={styles.productSupportLayout}
            data-kontent-item-id={kontentItemId}
        >
            {(documents?.length > 0 || mappedDocumentGroups.length > 0) && (
                <div className={styles.documentationContainer}>
                    <div className={styles.documentationRow}>
                        <h2 className={styles.docMobileTitle}>
                            Product Documentation
                        </h2>
                        <div
                            className={styles.docImage}
                            data-kontent-element-codename="gallery_images"
                        >
                            <LazyImage
                                src={optimizeKontentImageUrl(
                                    documentImage?.url,
                                    { ...imagePreset.card.desktop, q: 95 }
                                )}
                                srcSet={buildKontentSrcSet(
                                    documentImage?.url,
                                    imagePreset.card.desktop.w,
                                    { fm: 'webp' as const, q: 95 }
                                )}
                                sizes="(max-width: 768px) 100vw, 50vw"
                                alt={documentImage?.description || ''}
                                {...(documentImage?.width &&
                                documentImage?.height
                                    ? getDisplayDimensions(
                                          documentImage.width,
                                          documentImage.height,
                                          imagePreset.card.desktop.w
                                      )
                                    : // Not queried yet: common manual-cover
                                      // ratio (773x1000).
                                      { width: 800, height: 1035 })}
                            />
                        </div>
                        <div>
                            <h2 className={styles.docDesktopTitle}>
                                Product Documentation
                            </h2>
                            <div className={styles.docsInfo}>
                                <p>{productName}:</p>
                                <ul className={styles.docListContainer}>
                                    {documents.map((doc, id) => (
                                        <li key={'doc' + doc.url}>
                                            <a
                                                href={doc.url}
                                                rel={
                                                    doc?.url?.endsWith('.pdf')
                                                        ? 'nofollow'
                                                        : ''
                                                }
                                            >
                                                {doc.name ||
                                                    doc.typeName ||
                                                    doc.url}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                                {mappedDocumentGroups?.length > 0 && (
                                    <>
                                        {mappedDocumentGroups.map(
                                            (group, id) => (
                                                <details
                                                    key={`group-${id}`}
                                                    className={styles.docGroup}
                                                >
                                                    <summary
                                                        className={
                                                            styles.docGroupTitle
                                                        }
                                                    >
                                                        {group.name}
                                                    </summary>
                                                    <ul
                                                        className={
                                                            styles.docGroupList
                                                        }
                                                    >
                                                        {group.documents.map(
                                                            (doc, idx) => (
                                                                <li
                                                                    key={
                                                                        'doc' +
                                                                        doc.url +
                                                                        idx
                                                                    }
                                                                >
                                                                    <a
                                                                        href={
                                                                            doc.url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer nofollow"
                                                                    >
                                                                        {doc.name ||
                                                                            doc.typeName ||
                                                                            doc.url}
                                                                    </a>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </details>
                                            )
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div>
                {quickTiles?.length !== 0 && (
                    <div
                        style={{ maxWidth: `${quickTiles?.length * 275}px` }}
                        className={styles.quickTilesContainer}
                    >
                        <div className={styles.quickTiles}>
                            {quickTiles?.map((tile) => (
                                <a
                                    key={tile?.url}
                                    href={tile.url}
                                    className={styles.quickTile}
                                >
                                    <div
                                        className={`${styles.quickTileImg} ${
                                            tile?.image?.type ===
                                            'kontent_item_media___icon'
                                                ? styles.quickTileIcon
                                                : ''
                                        }`}
                                        data-kontent-element-codename="image"
                                    >
                                        <LazyImage
                                            src={
                                                (tile?.image?.type ===
                                                'kontent_item_media___icon'
                                                    ? tile.image.url
                                                    : optimizeKontentImageUrl(
                                                          tile.image.url,
                                                          {
                                                              ...imagePreset
                                                                  .card.desktop,
                                                              q: 95,
                                                          }
                                                      )) as string
                                            }
                                            srcSet={
                                                tile?.image?.type ===
                                                'kontent_item_media___icon'
                                                    ? undefined
                                                    : buildKontentSrcSet(
                                                          tile.image.url,
                                                          imagePreset.card
                                                              .desktop.w,
                                                          {
                                                              fm: 'webp' as const,
                                                              q: 95,
                                                          }
                                                      )
                                            }
                                            sizes={
                                                tile?.image?.type ===
                                                'kontent_item_media___icon'
                                                    ? undefined
                                                    : '(max-width: 576px) 100vw, 25vw'
                                            }
                                            alt={tile.image.description || ''}
                                            width={275}
                                            height={200}
                                        />
                                    </div>
                                    <p>{tile.label}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                )}
                {videos?.length !== 0 && (
                    <div className={styles.videos}>
                        <h2>Helpful Videos</h2>
                        <div className={styles.videosContainer}>
                            {videos?.map((video) => (
                                <div
                                    key={video.videoUrl}
                                    className={styles.videoContainer}
                                >
                                    <div>
                                        <VideoPlayer
                                            src={video.videoUrl}
                                            thumbnail={video.image?.url || true}
                                        />
                                    </div>
                                    <p>{video.caption}</p>
                                </div>
                            ))}
                        </div>
                        <Modal
                            contentLabel="Product Support"
                            isOpen={modalActive}
                            onRequestClose={closeModal}
                            ariaHideApp={false}
                            style={{
                                content: {
                                    padding: '0',
                                },
                            }}
                        >
                            <ReactPlayer
                                url={activeVideo}
                                width="100%"
                                height="100%"
                                playing={true}
                                controls
                            />
                        </Modal>
                    </div>
                )}
                {safeFaqs.length > 0 && (
                    <div className={styles.faq}>
                        <h2 ref={headingRef} id={faqanchor}>
                            {headline || 'Frequently Asked Questions'}
                        </h2>
                        {safeFaqs.map((faq, id) => (
                            <Faq
                                key={faq?.id || `faq${id}`}
                                faq={faq}
                                truncateBody={true}
                                isDisplayHidden={id >= visibleFaqCount}
                            />
                        ))}
                        {renderViewMore()}
                    </div>
                )}
            </div>
        </section>
    );
};

export default ProductSupportLayout;
